import React, { useEffect, useState } from 'react';
import { useUserData } from '../../hooks/useUserData';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { formatUsdc, bnToNumber } from '../../utils/format';
import { RANK_NAMES, NUM_RANKS, NUM_MILESTONES } from '../../utils/constants';
import { NetworkGuard } from '../wallet/NetworkGuard';
import { InlineLoader, ButtonSpinner } from '../common/Loader';
import { Alert } from '../common/StatBox';
import { parseError } from '../../utils/errors';
import { sendTx } from '../../utils/txHelpers';
import { ACTIVE_NETWORK } from '../../config/appConfig';

const RANK_ICONS = ['🥈', '🥇', '💠', '💎', '⭐'];

// Cache keys — bump the suffix if contract config changes
const CACHE_RANKS = `zenova_ranks_${ACTIVE_NETWORK}_v1`;
const CACHE_MILES = `zenova_miles_${ACTIVE_NETWORK}_v1`;

// Serialize BigNumbers to plain strings so JSON.stringify works.
const serializeRankReqs = (arr) =>
  arr.map((r) => ({
    selfInvestment: r.selfInvestment.toString(),
    directs: r.directs.toString(),
    teamVolume: r.teamVolume.toString(),
    requiredLegRank: r.requiredLegRank,
    requiredLegCount: r.requiredLegCount
  }));
const serializeMilestones = (arr) =>
  arr.map((r) => ({
    activeMembers: r.activeMembers.toString(),
    teamVolume: r.teamVolume.toString()
  }));

const rehydrateRankReqs = (arr) => {
  const { BigNumber } = require('ethers');
  return arr.map((r) => ({
    selfInvestment: BigNumber.from(r.selfInvestment),
    directs: BigNumber.from(r.directs),
    teamVolume: BigNumber.from(r.teamVolume),
    requiredLegRank: r.requiredLegRank,
    requiredLegCount: r.requiredLegCount
  }));
};
const rehydrateMilestones = (arr) => {
  const { BigNumber } = require('ethers');
  return arr.map((r) => ({
    activeMembers: BigNumber.from(r.activeMembers),
    teamVolume: BigNumber.from(r.teamVolume)
  }));
};

const readCache = (key, rehydrate) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return rehydrate(JSON.parse(raw));
  } catch (e) { return null; }
};
const writeCache = (key, data) => {
  try { sessionStorage.setItem(key, JSON.stringify(data)); } catch (e) {}
};

export const Ranks = () => (
  <NetworkGuard>
    <RanksInner />
  </NetworkGuard>
);

const RanksInner = () => {
  const { userId, isRegistered, basic, extended, refresh } = useUserData();
  const { viewsR, logicW } = useContracts();
  const notify = useNotify();

  // Static config — cached in sessionStorage since it rarely changes
  const [reqs, setReqs] = useState(() => readCache(CACHE_RANKS, rehydrateRankReqs));
  const [milestoneReqs, setMilestoneReqs] = useState(() => readCache(CACHE_MILES, rehydrateMilestones));

  // Per-user data
  const [claimable, setClaimable] = useState(null);
  const [milestoneStatus, setMilestoneStatus] = useState(null);

  // Section-level loading + error state
  const [ranksLoading, setRanksLoading] = useState(!reqs);
  const [milesLoading, setMilesLoading] = useState(!milestoneReqs);
  const [ranksErr, setRanksErr] = useState(null);
  const [milesErr, setMilesErr] = useState(null);

  const [claiming, setClaiming] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [reload, setReload] = useState(0);

  // ==== Load rank requirements (independent) ====
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      setRanksErr(null);
      // Only show loader if we don't have cached data yet
      if (!reqs) setRanksLoading(true);
      try {
        // Fire in parallel with individual error tolerance so one bad call doesn't kill others
        const results = await Promise.all(
          Array.from({ length: NUM_RANKS }).map((_, i) =>
            viewsR.getRankRequirement(i).catch((e) => ({ __err: e }))
          )
        );
        if (cancelled) return;
        const bad = results.find((r) => r && r.__err);
        if (bad) throw bad.__err;
        setReqs(results);
        writeCache(CACHE_RANKS, serializeRankReqs(results));
      } catch (e) {
        if (!cancelled) setRanksErr('Failed to load rank requirements');
      } finally {
        if (!cancelled) setRanksLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [viewsR, reload]);

  // ==== Load milestone requirements (independent) ====
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      setMilesErr(null);
      if (!milestoneReqs) setMilesLoading(true);
      try {
        const results = await Promise.all(
          Array.from({ length: NUM_MILESTONES }).map((_, i) =>
            viewsR.getMilestoneRequirement(i).catch((e) => ({ __err: e }))
          )
        );
        if (cancelled) return;
        const bad = results.find((r) => r && r.__err);
        if (bad) throw bad.__err;
        setMilestoneReqs(results);
        writeCache(CACHE_MILES, serializeMilestones(results));
      } catch (e) {
        if (!cancelled) setMilesErr('Failed to load milestones');
      } finally {
        if (!cancelled) setMilesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [viewsR, reload]);

  // ==== Load user's claimable pools (only when registered) ====
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !userId) return;
      try {
        const cl = await viewsR.getUserAllClaimableRankPools(userId);
        if (!cancelled) setClaimable(cl);
      } catch (e) { /* silent — non-critical */ }
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, userId, reload]);

  // ==== Load user's milestone status (only when registered) ====
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR || !userId) return;
      try {
        const ms = await viewsR.getUserMilestoneStatus(userId);
        if (!cancelled) setMilestoneStatus(ms);
      } catch (e) { /* silent */ }
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, userId, reload]);

  if (!isRegistered) return <Alert type="info">Register and invest to unlock ranks and milestones.</Alert>;

  const currentRank = extended?.rank || 0;

  const claimOne = async (rankIdx) => {
    if (!logicW) return;
    setClaiming(rankIdx);
    try {
      await sendTx(logicW, 'claimRankPool', [rankIdx], { notify, label: `Claim ${RANK_NAMES[rankIdx + 1]} pool` });
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setClaiming(null); }
  };

  const claimAll = async () => {
    if (!logicW) return;
    setClaiming('all');
    try {
      await sendTx(logicW, 'claimAllRankPools', [], { notify, label: 'Claim all rank pools' });
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setClaiming(null); }
  };

  const checkPromotion = async () => {
    if (!logicW) return;
    setPromoting(true);
    try {
      await sendTx(logicW, 'checkAndPromoteRank', [userId], { notify, label: 'Check promotion' });
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setPromoting(false); }
  };

  return (
    <div>
      {/* Header (renders instantly from useUserData) */}
      <div className="user-card">
        <div className="user-card-top">
          <div className="user-avatar-ring">
            <svg viewBox="0 0 68 68"><circle cx="34" cy="34" r="31" fill="none" stroke="#ffb800" strokeWidth="2" strokeDasharray="6 4" /></svg>
            <div className="inner-avatar" style={{ background: 'var(--grad-gold)' }}>🏆</div>
          </div>
          <div className="user-card-info">
            <div className="user-card-id">{RANK_NAMES[currentRank] || 'Unranked'}</div>
            <div className="user-card-wallet">Your current rank</div>
          </div>
        </div>
        <div className="flex" style={{ gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={checkPromotion} disabled={promoting}>
            {promoting && <ButtonSpinner />}Check Promotion
          </button>
          {claimable && !claimable.total.isZero() && (
            <button className="btn btn-gold btn-sm" onClick={claimAll} disabled={claiming === 'all'}>
              {claiming === 'all' && <ButtonSpinner />}Claim All ${formatUsdc(claimable.total)}
            </button>
          )}
        </div>
      </div>

      {/* ==================== RANKS ==================== */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">🏆</span> Rank Progress</span>
          {ranksLoading && reqs && <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Refreshing…</span>}
        </div>

        {ranksErr && !reqs && (
          <Alert type="error">
            {ranksErr}. <button onClick={() => setReload((r) => r + 1)} style={{ background: 'transparent', color: 'var(--neon-cyan)', textDecoration: 'underline', cursor: 'pointer' }}>Retry</button>
          </Alert>
        )}

        {!reqs && ranksLoading && <InlineLoader text="Loading ranks..." />}

        {reqs && reqs.map((req, idx) => {
          const rankNum = idx + 1;
          const achieved = currentRank >= rankNum;
          const userClaimable = claimable ? claimable.amounts[idx] : null;

          const selfPct = basic && !req.selfInvestment.isZero()
            ? Math.min(100, Number(basic.totalInvestment.mul(100).div(req.selfInvestment)))
            : (achieved ? 100 : 0);
          const dirPct = extended && bnToNumber(req.directs) > 0
            ? Math.min(100, (bnToNumber(extended.activeDirects) / bnToNumber(req.directs)) * 100)
            : (achieved ? 100 : 0);
          const volPct = extended && !req.teamVolume.isZero()
            ? Math.min(100, Number(extended.teamVolume.mul(100).div(req.teamVolume)))
            : (achieved ? 100 : 0);
          const overall = achieved ? 100 : Math.min(selfPct, dirPct, volPct);

          const selfMet = basic && basic.totalInvestment.gte(req.selfInvestment);
          const dirMet  = extended && bnToNumber(extended.activeDirects) >= bnToNumber(req.directs);
          const volMet  = extended && extended.teamVolume.gte(req.teamVolume);

          return (
            <div key={idx} className={`rank-mini ${achieved ? 'achieved' : ''}`}>
              <div className="rank-mini-header">
                <div className="rank-emoji">{RANK_ICONS[idx]}</div>
                <div style={{ flex: 1 }}>
                  <div className="rank-mini-title">{RANK_NAMES[rankNum]}</div>
                  <div className="rank-mini-earned">
                    {achieved ? '✓ Achieved' : `${overall.toFixed(0)}% complete`}
                  </div>
                </div>
                {achieved && <span className="badge badge-gold">Active</span>}
              </div>

              <div>
                <div className="rank-req-row">
                  <span className="k">Self Investment</span>
                  <span className={`v ${selfMet ? 'met' : 'unmet'}`}>${formatUsdc(req.selfInvestment)}</span>
                </div>
                <div className="rank-req-row">
                  <span className="k">Active Directs</span>
                  <span className={`v ${dirMet ? 'met' : 'unmet'}`}>{bnToNumber(req.directs)}</span>
                </div>
                <div className="rank-req-row">
                  <span className="k">Team Volume</span>
                  <span className={`v ${volMet ? 'met' : 'unmet'}`}>${formatUsdc(req.teamVolume)}</span>
                </div>
                {bnToNumber(req.requiredLegCount) > 0 && (
                  <div className="rank-req-row">
                    <span className="k">Legs Required</span>
                    <span className="v">{bnToNumber(req.requiredLegCount)}× {RANK_NAMES[req.requiredLegRank]}</span>
                  </div>
                )}
              </div>

              <div className="rank-progress-bar">
                <div className="rank-progress-fill" style={{ width: `${overall}%` }} />
              </div>

              {achieved && userClaimable && !userClaimable.isZero() && (
                <div className="rank-earned-strip">
                  <span className="lbl">💎 You've earned</span>
                  <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                    <span className="amt">${formatUsdc(userClaimable)}</span>
                    <button className="btn btn-primary btn-sm" onClick={() => claimOne(idx)} disabled={claiming === idx}>
                      {claiming === idx && <ButtonSpinner />}
                      Claim
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ==================== MILESTONES ==================== */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">⭐</span> Leadership Milestones</span>
          {milesLoading && milestoneReqs && <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Refreshing…</span>}
        </div>

        {milesErr && !milestoneReqs && (
          <Alert type="error">
            {milesErr}. <button onClick={() => setReload((r) => r + 1)} style={{ background: 'transparent', color: 'var(--neon-cyan)', textDecoration: 'underline', cursor: 'pointer' }}>Retry</button>
          </Alert>
        )}

        {!milestoneReqs && milesLoading && <InlineLoader text="Loading milestones..." />}

        {milestoneReqs && milestoneReqs.map((r, i) => {
          const eligible = milestoneStatus ? milestoneStatus.eligible[i] : false;
          const rewarded = milestoneStatus ? milestoneStatus.rewarded[i] : false;
          const cls = rewarded ? 'rewarded' : eligible ? 'eligible' : '';
          const members = bnToNumber(r.activeMembers);
          const curMembers = extended ? bnToNumber(extended.activeMembersDownline) : 0;

          return (
            <div key={i} className={`milestone-mini ${cls}`}>
              <div className="milestone-num-mini">{i + 1}</div>
              <div className="milestone-mini-info">
                <div className="milestone-mini-title">Milestone #{i + 1}</div>
                <div className="milestone-mini-progress">
                  {curMembers}/{members} members · ${formatUsdc(extended?.teamVolume)}/${formatUsdc(r.teamVolume)}
                </div>
              </div>
              {rewarded && <span className="badge badge-gold">✓ Rewarded</span>}
              {!rewarded && eligible && <span className="badge badge-green">Eligible</span>}
              {!rewarded && !eligible && <span className="badge badge-muted">In Progress</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
